import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import {
  Body1,
  Body2,
  firestore,
  KeyboardSafeAreaScrollView,
  Subtitle1,
  Touchable,
  useTheme,
  VectorIcon,
} from "capsule"
import firebase from "firebase/compat/app"
import { useFormikContext } from "formik"
import React, { useEffect, useMemo, useState } from "react"
import { useCollectionData } from "react-firebase-hooks/firestore"
import { useTranslation } from "react-i18next"
import { StyleSheet, View } from "react-native"

import { Organ } from "../../common/Pathology"
import { collections } from "../../common/types"
import { INPUT_HEIGHT } from "../../features/config/Constants"
import { itemName } from "../../features/hooks/useItemI18n"
import { NewClientOnboardingNS } from "../../features/i18n/constants"
import { NewClientOnboardingParamList } from "../../features/Navigation/types"

const NewClientPatientPathologyIntroScreen = () => {
  const { t } = useTranslation(NewClientOnboardingNS)
  const navigation =
    useNavigation<
      StackNavigationProp<NewClientOnboardingParamList, "NewClientPatientPathologyIntro">
    >()

  // Use parent Formik context to get organ from OrganSelectScreen
  const parentFormik = useFormikContext<{ organ?: string }>()

  const {
    colors: {
      surface: { background },
      primary,
    },
    dimensions: { spacing },
  } = useTheme()

  // Fetch organs to get the label
  const [organs] = useCollectionData<Organ>(
    firestore()
      .collection(collections.ORGANS)
      .where("specialty", "==", "ortho")
      .where("disabled", "==", false) as unknown as firebase.firestore.Query<Organ>,
    { idField: "id" },
  )

  const [selectedOrganLabel, setSelectedOrganLabel] = useState<string>("")

  // Update organ label when organ changes or organs data loads
  useEffect(() => {
    if (parentFormik.values.organ && organs) {
      const organ = organs.find(o => o.id === parentFormik.values.organ)
      if (organ) {
        setSelectedOrganLabel(itemName(organ, t))
      }
    }
  }, [parentFormik.values.organ, organs, t])

  const s = useMemo(
    () => ({
      container: [
        styles.container,
        {
          backgroundColor: background,
        },
      ],
      innerContent: {
        paddingBottom: spacing * 2,
        paddingHorizontal: spacing * 2,
        gap: spacing * 2,
      },
      headerContainer: {
        gap: spacing,
      },
      organField: [
        styles.organField,
        {
          height: INPUT_HEIGHT,
          borderBottomColor: primary,
          paddingHorizontal: spacing / 4,
          marginBottom: spacing,
        },
      ],
    }),
    [background, spacing, primary],
  )

  return (
    <KeyboardSafeAreaScrollView style={s.container} contentContainerStyle={s.innerContent}>
      <View style={s.headerContainer}>
        <Body1 style={styles.bold}>{t("patientPathologyIntro.title")}</Body1>
        <Body1 style={styles.bold}>{t("patientPathologyIntro.subtitle")}</Body1>
        <Body2>{t("patientPathologyIntro.info")}</Body2>
      </View>

      <Touchable
        onPress={() => navigation.navigate("NewClientOrganSelect")}
        style={s.organField}
        testID="button_organ"
      >
        <View style={styles.organFieldContent}>
          <Subtitle1 style={parentFormik.values.organ ? undefined : styles.organPlaceholder}>
            {selectedOrganLabel || t("patientPathologyIntro.organPlaceholder")}
          </Subtitle1>
          <VectorIcon name="chevron-right" category="MaterialIcons" size={24} />
        </View>
      </Touchable>
    </KeyboardSafeAreaScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  organField: {
    justifyContent: "center",
    borderBottomWidth: 1,
  },
  organFieldContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: "100%",
  },
  organPlaceholder: {
    opacity: 0.5,
  },
  bold: {
    fontWeight: "bold",
  },
})

export default NewClientPatientPathologyIntroScreen
